import React, { useEffect, useState } from "react";
import { Form, ModalDialog, StringField, SubmitButton } from "@culturehq/components";

import { useDoor, useFetch, useSignUpload, useStoryQuestions, useTryCatch } from "app/hooks";
import { Trans } from "app/utils";
import ErrorMessage from "components/common/ErrorMessage";
import ChqModalDialog from "components/common/ChqModalDialog";
import { StoryQuestion as StoryQuestionType } from "chq";

import SelectImage from "components/common/SelectImage";
import StoryQuestion from "components/stories/StoryQuestion";
import ProgressLine from "components/admin/analytics/ProgressLine";
import tagsTypes from "constants/tagsTypes";
import SelectUsers from "components/SelectUsers";
import { DefaultPrompt } from "./DefaultPromptTypes";

import "./default-prompts.scss";

type PartialDefaultPrompt = Partial<DefaultPrompt>;
interface DefaultPromptModalProps {
  children: (callback: () => void) => React.ReactNode;
  defaultPrompt?: DefaultPrompt;
  onCreate?: (defaultPrompt: DefaultPrompt) => void;
  onUpdate?: (defaultPrompt: DefaultPrompt) => void;
}

const DefaultPromptModal = ({
  children,
  defaultPrompt,
  onCreate = () => {},
  onUpdate = () => {}
}: DefaultPromptModalProps) => {
  const { open, onOpen, onClose } = useDoor(false);
  const [selectedPrompt, setSelectedPrompt] = useState<StoryQuestionType | null>(null);
  const [selectedInviterId, setSelectedInviterId] = useState<number | undefined>(undefined);
  const [originalBannerImage, setOriginalBannerImage] = useState<string | File>();
  const [progress, setProgress] = useState<number>();

  const { getStoryQuestions } = useStoryQuestions();
  const signUpload = useSignUpload();

  useEffect(() => {
    if (open && defaultPrompt) {
      setSelectedPrompt(defaultPrompt?.prompt);
      setSelectedInviterId(defaultPrompt.defaultInviter.id);
      setOriginalBannerImage(defaultPrompt.imageUrl);
    } else {
      setSelectedPrompt(null);
      setSelectedInviterId(undefined);
      setProgress(undefined);
    }
  }, [defaultPrompt, open]);

  const {
    fetching,
    json: { storyQuestions = [] }
  } = useFetch(
    () => getStoryQuestions({
      answerable: selectedPrompt?.question ? undefined : true,
      all: !!selectedPrompt?.question
    }),
    [selectedPrompt?.question]
  );

  const getImageUrl = () => {
    const mediaUpload = 2;
    if (typeof originalBannerImage === "string") return originalBannerImage;

    return signUpload(originalBannerImage, (mediaProgress: number) => {
      const localProgress = (100 / mediaUpload) + (mediaProgress / mediaUpload);
      setProgress(localProgress);
    });
  };

  const [{ error }, onSubmit] = useTryCatch(
    async (values: PartialDefaultPrompt) => {
      const newImageUrl = await getImageUrl();
      const payload = {
        ...(defaultPrompt || {}),
        ...values,
        promptId: selectedPrompt?.id as unknown as number,
        imageUrl: newImageUrl,
        defaultInviterId: selectedInviterId
      };

      if (defaultPrompt) onUpdate(payload as DefaultPrompt);
      else onCreate(payload as DefaultPrompt);

      onClose();
    },
    [defaultPrompt, selectedPrompt, selectedInviterId, originalBannerImage]
  );

  if (error && error.status !== 422) {
    throw error;
  }

  const ModalTitle = () => (
    <Trans>
      {defaultPrompt ? "Edit default prompt" : "Add default prompt"}
    </Trans>
  );

  const handleBannerImageSelected = (originalImage: string | File, image: File) => {
    setOriginalBannerImage(image);
  };

  const handleSelectQuestion = (selectedQuestion: any) => {
    setSelectedPrompt(selectedQuestion);
  };

  return (
    <>
      {children(onOpen)}
      {open && (
        <ChqModalDialog onClose={onClose}>
          <ModalDialog.Heading onClose={onClose}>
            <ModalTitle />
          </ModalDialog.Heading>
          <ModalDialog.Body>
            {error && <ErrorMessage error={error} />}
            <StoryQuestion
              story={{ question: selectedPrompt?.question, body: null }}
              fetching={fetching}
              storyQuestions={storyQuestions}
              onSelectQuestion={handleSelectQuestion}
            />
            <div className="tags-container">
              <Trans>Inviter</Trans>
              <SelectUsers
                value={selectedInviterId}
                multiple={false}
                placeholder="Select inviter"
                options={!defaultPrompt ? null : [
                  {
                    category: tagsTypes.USERS,
                    categoryIcon: "user",
                    icon: "user-filled",
                    label: defaultPrompt.defaultInviter.name,
                    value: defaultPrompt.defaultInviter.id
                  }
                ]}
                onChange={setSelectedInviterId}
              />
            </div>
            <Form
              onSubmit={onSubmit}
              initialValues={{
                note: defaultPrompt?.note,
                imageUrl: defaultPrompt?.imageUrl
              }}
            >
              <SelectImage
                imageToShow={defaultPrompt?.imageUrl}
                onImageSelected={handleBannerImageSelected}
              />
              <StringField name="note" required>
                <Trans>Note</Trans>
              </StringField>
              {progress && (
                <div className="progressline-trend progress-bar">
                  <ProgressLine
                    className="story-progressbar"
                    color="#79b17d"
                    valueNow={progress}
                    valueMax={100}
                  />
                </div>
              )}
              <SubmitButton primary>
                {submitting => (
                  submitting ? <Trans>Submitting...</Trans> : <Trans>Save</Trans>
                )}
              </SubmitButton>
            </Form>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
    </>
  );
};

export default DefaultPromptModal;
